import { createResyncChannel, type ResyncChannelHandle } from "@/lib/realtime/channel";
import type { DocumentChangeCallbacks } from "./types";

/**
 * Subscribe to document changes for a building with granular event handling.
 * Provides separate callbacks for INSERT, UPDATE, DELETE events.
 *
 * The resync-on-re-join behaviour this relied on now lives in
 * `@/lib/realtime/channel`, shared with every other subscriber.
 */
export function subscribeToChanges(
  buildingId: string,
  callbacks: DocumentChangeCallbacks
): ResyncChannelHandle {
  const filter = `building_id=eq.${Number(buildingId)}`;

  // The whole handle, not just the channel: tearing down through
  // `supabase.removeChannel` alone would skip the handle's latch, so a late
  // event or a trailing SUBSCRIBED would still call back into a store that has
  // already moved on.
  return createResyncChannel({
    // Historic topic shape, kept verbatim — it is a label, and the module adds
    // the instance suffix that keeps a remount off the outgoing channel.
    topic: `documents_changes_${buildingId}`,
    bindings: [
      {
        event: "INSERT",
        table: "documents",
        filter,
        handler: (payload) => callbacks.onInsert?.(payload.new),
      },
      {
        event: "UPDATE",
        table: "documents",
        filter,
        handler: (payload) => callbacks.onUpdate?.(payload.old, payload.new),
      },
      {
        event: "DELETE",
        table: "documents",
        filter,
        handler: (payload) => callbacks.onDelete?.(payload.old),
      },
    ],
    onResync: () => callbacks.onResync?.(),
  });
}

/**
 * Unsubscribe from a realtime subscription.
 */
export function unsubscribeChannel(subscription: ResyncChannelHandle): void {
  subscription.unsubscribe();
}
